/**
 * **Resolution events** (`decisions/0211`): a resolution stage that owes a cost no longer builds
 * the step for it. It raises a {@link GameEvent} into the action's event loop ({@link emitEvent},
 * `events/loop.ts`, `decisions/0212`), where it lands pending on the resolution ledger, and the
 * resolver that owns the ledger settles every pending event once, at its end ({@link settleEvents}),
 * into ordinary `ResolvedStep`s. Steps stay the record: `applyResolved`
 * replays them rules-free and undo inverts them, exactly as before. Events never leave the
 * resolution and never reach the wire.
 *
 * **Replacements.** Before a charge spend grounds, settlement runs it past the `replacement`
 * clauses on the board ("if this would happen, instead …"). A replacement reads facts that exist
 * only once every die is drawn (the declaration's first attack roll), which is why costs settle at
 * the end: Ammo Case III's Efficiency waives a round's charges when the first roll crits.
 *
 * **No event may be silently lost** (`decisions/0211` §4). Every ledger declares at creation what
 * happens to what is raised into it ({@link LedgerSettlement}); raising into a ledger nobody settles,
 * or into one already settled, throws {@link UnsettledEventError}.
 *
 * **Every cost goes through here** (`event-engine-costs`, `decisions/0213`). The kinds are listed
 * on {@link GameEvent}; {@link settleEvents} grounds the steps, and {@link economyCommands},
 * {@link reactionSpendCommands} and {@link consumeStatusCommands} the sibling commands. A stage that
 * owns no settling ledger settles in place ({@link spendStepsNow}, {@link capUseSteps},
 * {@link economyNow}). {@link affordable} is the one check per kind, and the GM-override floor is
 * {@link SpendPolicy} on the settlement context. `costSettlement.pin.test.ts` fails on a cost step
 * or spend command built outside this directory.
 *
 * Purity: the ledger is created per resolution and thrown away on a halt, so a re-run after
 * `needs_decision` raises every event again from scratch.
 */

import { canUseAction, canUseLimitedWeapon, canUseProtocolAction, canUseReaction, canUseRoundAction, canUseSceneAction, freeActionUseKey, reactionSlotOpen, isWeaponLoaded, loadedResourceKey, resourceCapAvailable, resourceCapKey, type AffordanceUseSetStep, type CostWaivedStep, type DischargeSetStep, type EarmarkVerb, type EncounterCommand, type EncounterState, type ResourceDeltaStep, type ResourceSetStep, type StatusRemoveStep } from "@lancer-vtt/domain";
import { gatherReplacements, type BoundReplacement, type LoadedOverlay } from "../effects/loader";
import type { CostReplacementEffect } from "../effects/atoms";
import { createResolutionLedger, type ResolutionLedger } from "../effects/resolutionLedger";
import { createEventLoop, type EventLoop } from "./loop";

/** One use of a `consumeResourceOnApply` cadence cap (the counter `resourceCapKey(key)` ticks by 1). */
export type ResourceCapUseEvent = { kind: "resource_cap_use"; carrier: string; key: string };
/**
 * Spend `amount` charges of the carrier's Limited pool `pool` (its ledger slot): an Ammo Case round,
 * Custom Paint Job's once-per-Full-Repair charge. `read` is the pool's spent count when the raiser
 * reads it off a cascade-local overlay rather than the state settlement sees (Custom Paint Job,
 * mid structure cascade); `uses: undefined` there means the pool has no ledger entry yet.
 */
export type ExpendChargesEvent = { kind: "expend_charges"; carrier: string; pool: string; amount: number; read?: { uses: number | undefined } };
/**
 * What declaring `action` costs `carrier` (`decisions/0212` item 9), raised beside the declaration.
 * It settles even when a replacement prevents the action, unless that replacement refunds it
 * (`refunded`). The kind and its settlement only: `event-engine-costs` moves the real spends onto it.
 */
export type DeclarationCostEvent = { kind: "declaration"; carrier: string; action: string; refunded?: true };
/**
 * What firing a weapon, or activating a Recharge action, spends (`event-engine-costs` D1, 3.1),
 * on its affordance-ledger `key` (a weapon's instance key, or the action's record key):
 *   - `discharge`: a Recharge item becomes discharged until a start-of-turn recharge roll;
 *   - `loading`: a Loading weapon becomes unloaded;
 *   - `limited`: a Limited weapon spends one of its `limit` charges.
 * Each grounds against the state it settles over and is a no-op when already spent: a second
 * discharge, an unloaded weapon, a pool at its limit. The last is the floor (`decisions/0209` §3):
 * a GM-forced fire past the limit spends nothing more, so a refunded charge makes the weapon
 * fireable again rather than leaving it over-counted.
 */
export type WeaponCostEvent = { kind: "weapon"; carrier: string; key: string; spend: "discharge" | "loading" | "limited"; limit?: number };
/**
 * A status spent by using it (`event-engine-costs` D1, 3.3): Lock On consumed by the attack that
 * took its +1, Bolster by the save or check it boosted. `facets` are the consumed instance's, so
 * the remove's inverse re-adds that exact instance on undo (Bolster records them; Lock On's clear
 * never has). One action spends a status once.
 */
export type ConsumeStatusEvent = { kind: "consume_status"; carrier: string; status: string; facets?: Pick<StatusRemoveStep, "expiry" | "appliedTurn" | "source"> };
/**
 * A counter a use moves by `delta` (`event-engine-costs` D1, 3.4): the Student-Class NHP's 1/round
 * reroll and Sea Legs' round use (+1 on their cap key), a Patch or a cover repair's Repair Cap (-1).
 * Grounds to one `resource_delta`; every use counts.
 */
export type CounterCostEvent = { kind: "counter"; carrier: string; key: string; delta: number };
/**
 * One reaction used (`event-engine-costs` D8, Phase 4): `carrier` spends its reaction on `name`.
 * Raised where the reaction is taken ({@link useReaction}); settled into the action's
 * `reactionSpentBy`, which the referee grounds as one sibling `spend_reaction` per use, in raise
 * order. A reactor with a use pending is spent for every later reaction gate in the action
 * ({@link reactorsUsed}).
 */
export type ReactionUseEvent = { kind: "reaction_use"; carrier: string; name: string };
/**
 * What declaring an action costs off the acting unit's turn budget (`event-engine-costs` D1, 5.1):
 * `points` action points, and the slot the use stamps, either the once-per-turn `actionId` (a
 * Quick Tech option's own slot included) or the Protocol ledger's `protocolActionId`. `reaction`
 * marks a reaction attack (Overwatch, Covering Fire's shot): `carrier` spends its reaction too, on
 * `reaction.name` (omitted: `"overwatch"`). Grounds to one `spend_action`.
 */
export type ActionPointsEvent = { kind: "action_points"; carrier: string; points: number; reaction?: { name?: string }; actionId?: string; protocolActionId?: string };
/**
 * One use of an affordance's round or scene rate, or of its Limited pool (`decisions/0202`, D1):
 * stamped at `key` (the pool at `pool ?? key`) on `carrier`'s `grain` ledger. Grounds to one
 * `spend_action_use`.
 */
export type ActionUseEvent = { kind: "action_use"; carrier: string; key: string; grain: "round" | "scene"; max: number | undefined; pool?: string };
/** A held free-action earmark spent on the action it made free (D1): one `free_action_use_consume`. */
export type EarmarkEvent = { kind: "earmark"; carrier: string; verb: EarmarkVerb };
/** The action-economy costs: settled into the action's batch as sibling commands ({@link economyCommands}). */
export type EconomyCostEvent = ActionPointsEvent | ActionUseEvent | EarmarkEvent;
export type GameEvent = ResourceCapUseEvent | ExpendChargesEvent | DeclarationCostEvent | WeaponCostEvent | ConsumeStatusEvent | CounterCostEvent | ReactionUseEvent | EconomyCostEvent;

/** One to-hit roll this resolution made, in roll order (an area fan-out records one per caught target). */
export type AttackRollFact = { target: string; hit: boolean; crit: boolean };

/**
 * What a ledger's owner does with the events raised into it:
 *   - `"settles"`: the owner calls {@link settleEvents} at its end (`resolve()`);
 *   - `"discards"`: a probe whose whole output is thrown away (`previewAttackDeclaredChoice`, and
 *     the attack forecast's cost probe, which reads the raised costs before discarding them);
 *   - `"refuses"`: nothing settles it, so raising an event throws (`interpretEffect`'s fallback
 *     ledger for an activation, lifecycle, move or mine clause).
 */
export type LedgerSettlement = "settles" | "discards" | "refuses";

/** Raised when an event would be silently lost: no settle point, or raised after settlement. */
export class UnsettledEventError extends Error {
  constructor(event: GameEvent, why: string) {
    super(`resolution event ${event.kind} (${event.carrier}) ${why}`);
    this.name = "UnsettledEventError";
  }
}

/** A loop for a ledger whose owner gave it none (a probe, a test): it carries costs and draws no dice. */
const NO_DICE = { roll: (): number => { throw new Error("a cost-only event loop draws no dice"); } };

/**
 * Raise a cost into the action's event loop (`events/loop.ts`, `decisions/0212`): it is proposed,
 * lands as pending on the ledger, and settles once at the end of the current resolution. The ledger
 * is the action's scope: its pending list is what {@link settleEvents} grounds.
 */
export function emitEvent(ledger: ResolutionLedger, event: GameEvent): void {
  if (ledger.settlement === "refuses") throw new UnsettledEventError(event, "was raised where nothing settles the ledger");
  if (ledger.settled === true) throw new UnsettledEventError(event, "was raised after the ledger settled");
  const loop: EventLoop = (ledger.loop ??= createEventLoop({ dice: NO_DICE }));
  loop.raise({ kind: "cost", cost: event }, { happen: () => (ledger.events ??= []).push(event), trigger: () => undefined });
}

/**
 * The cap steps for a fire site outside `resolve()` (a turn or round edge, a move, damage taken, an
 * aura, a track change), which owns no resolution ledger: each use the fire consumed is raised into
 * the intent's loop as a cost event on a ledger of its own, and that ledger settles at once, in
 * raise order. Each use grounds as one `resource_delta +1`.
 */
export function capUseSteps(caps: readonly { carrier: string; key: string }[], loop: EventLoop | undefined): ResourceDeltaStep[] {
  if (caps.length === 0) return [];
  const ledger = createResolutionLedger("settles");
  if (loop !== undefined) ledger.loop = loop;
  for (const { carrier, key } of caps) emitEvent(ledger, { kind: "resource_cap_use", carrier, key });
  return settleEvents(ledger).capSteps;
}

/**
 * The spend steps for costs a stage records where it stands (a Recharge action's discharge, a
 * reaction's discharge, a consumed Lock On or Bolster, a reroll's or a Patch's counter, Custom
 * Paint Job's charge): raised into the intent's loop on a ledger
 * of their own and settled at once over `state`.
 */
export function spendStepsNow(costs: readonly SpendEvent[], state: EncounterState | undefined, loop: EventLoop | undefined): SpendStep[] {
  if (costs.length === 0) return [];
  const ledger = createResolutionLedger("settles");
  if (loop !== undefined) ledger.loop = loop;
  for (const cost of costs) emitEvent(ledger, cost);
  return settleEvents(ledger, state !== undefined ? { state, overlay: undefined } : undefined).steps;
}

/** Raise each reaction use into the action's ledger (D8): the one helper every reaction spend goes through. */
export function useReaction(ledger: ResolutionLedger, uses: readonly { combatantId: string; name: string }[] | undefined): void {
  for (const { combatantId, name } of uses ?? []) emitEvent(ledger, { kind: "reaction_use", carrier: combatantId, name });
}

/**
 * The reaction uses a stage outside `resolve()` took (a mine's or a meltdown's Intercede, a
 * trigger's, a Jockey's), raised through {@link useReaction} on a ledger of their own and settled
 * at once into its `reactionSpentBy`, in raise order. `undefined` when none was taken.
 */
export function reactionUsesNow(uses: readonly { combatantId: string; name: string }[], loop?: EventLoop): { combatantId: string; name: string }[] | undefined {
  if (uses.length === 0) return undefined;
  const ledger = createResolutionLedger("settles");
  if (loop !== undefined) ledger.loop = loop;
  useReaction(ledger, uses);
  return settleEvents(ledger).reactions;
}

/**
 * A status an action consumes as a sibling of its batch rather than a step of its record
 * (Executioner's Backswing Cut reservation, D1 5.3): raised as a `consume_status` cost, settled at
 * once, and grounded as today's `remove_status` command, one per status, for `actorId`'s batch.
 */
export function consumeStatusCommands(actorId: string, costs: readonly ConsumeStatusEvent[], loop?: EventLoop): EncounterCommand[] {
  return spendStepsNow(costs, undefined, loop).flatMap((s): EncounterCommand[] => (s.op === "status_remove" ? [{ type: "remove_status", actorId, combatantId: s.target, status: s.status }] : []));
}

/** A ledger for a stage's reaction uses (`InterceptSink`), on the intent's loop when the stage has one; its owner settles it. */
export function reactionLedger(loop: EventLoop | undefined): ResolutionLedger {
  const ledger = createResolutionLedger("settles");
  if (loop !== undefined) ledger.loop = loop;
  return ledger;
}

/** Settle a stage's reaction ledger into its result's `reactionSpentBy`, omitted when nobody reacted. */
export function settledReactions(ledger: ResolutionLedger): { reactionSpentBy?: { combatantId: string; name: string }[] } {
  const { reactions } = settleEvents(ledger);
  return reactions !== undefined ? { reactionSpentBy: reactions } : {};
}

/**
 * The grounding of settled reaction uses (D4): one sibling `spend_reaction` command each, in order,
 * for the caller's batch. Every `spend_reaction` the engine builds comes from here.
 */
export function reactionSpendCommands(actorId: string, uses: readonly { combatantId: string; name: string }[] | undefined): EncounterCommand[] {
  return (uses ?? []).map((s) => ({ type: "spend_reaction", actorId, combatantId: s.combatantId, name: s.name }));
}

/**
 * The grounding of settled economy costs (D4): one sibling command each, in raise order, for the
 * caller's batch. Every `spend_action`, `spend_action_use` and earmark consume the engine builds
 * comes from here.
 */
export function economyCommands(actorId: string, costs: readonly EconomyCostEvent[] | undefined): EncounterCommand[] {
  return (costs ?? []).map((c): EncounterCommand => {
    switch (c.kind) {
      case "action_points":
        return {
          type: "spend_action",
          actorId,
          points: c.points,
          ...(c.reaction !== undefined ? { reactionBy: c.carrier } : {}),
          ...(c.reaction?.name !== undefined ? { reactionName: c.reaction.name } : {}),
          ...(c.actionId !== undefined ? { actionId: c.actionId } : {}),
          ...(c.protocolActionId !== undefined ? { protocolActionId: c.protocolActionId } : {}),
        };
      case "action_use":
        return { type: "spend_action_use", actorId, combatantId: c.carrier, actionId: c.key, grain: c.grain, max: c.max, ...(c.pool !== undefined ? { pool: c.pool } : {}) };
      case "earmark":
        return { type: "apply_resolved", actorId, steps: [{ op: "resource_delta", target: c.carrier, key: freeActionUseKey(c.verb), delta: -1 }], kind: "free_action_use_consume" };
    }
  });
}

/**
 * How a resolution pays for itself (D5). `rules` is the gameplay door's: legality already proved
 * the unit may act, so it spends while a turn is acting. `forced` is the GM override's
 * (`decisions/0209` §3): costs are spent normally, floored at zero; the action spend and the
 * once-per-turn ledgers only on the forced unit's OWN turn (off it, the budget belongs to the
 * acting unit). Settlement applies it ({@link settleEvents}), so the floor lives in one place.
 */
export type SpendPolicy = { kind: "rules" } | { kind: "forced"; onTurn: boolean };

export const RULES_SPEND: SpendPolicy = { kind: "rules" };

/**
 * Whether an action resolved under `policy` spends its turn budget: under the rules, while a
 * turn is acting (legality already proved it is the actor's); forced, only on the actor's own turn.
 */
export function spendsThisTurn(state: EncounterState, policy: SpendPolicy): boolean {
  return policy.kind === "rules" ? state.activation.turn.phase === "acting" : policy.onTurn;
}

/**
 * The economy costs of an action that owns no other ledger (a deploy, a contest, a mount): raised
 * into the intent's loop (`at.loop`, when the caller has one) on a ledger of their own, settled at
 * once under `at.policy`, and grounded for `actorId`'s batch. Empty when settlement spends nothing.
 */
export function economyNow(actorId: string, costs: readonly EconomyCostEvent[], at: { state: EncounterState; policy: SpendPolicy; loop?: EventLoop }): EncounterCommand[] {
  if (costs.length === 0) return [];
  const ledger = createResolutionLedger("settles");
  if (at.loop !== undefined) ledger.loop = at.loop;
  for (const cost of costs) emitEvent(ledger, cost);
  return economyCommands(actorId, settleEvents(ledger, { state: at.state, overlay: undefined, policy: at.policy }).economy);
}

/**
 * Whether settlement spends an economy cost under the context's policy (D5, `decisions/0209` §3):
 * the turn budget's costs (action points, the earmark) only while {@link spendsThisTurn}; a forced
 * action's rate or pool use only while it has room, so the pool floors at zero. With no policy,
 * every cost raised is spent.
 */
function economySpends(cost: EconomyCostEvent, ctx: SettleContext | undefined): boolean {
  const policy = ctx?.policy;
  if (ctx === undefined || policy === undefined) return true;
  if (cost.kind !== "action_use") return spendsThisTurn(ctx.state, policy);
  return policy.kind === "rules" || affordable(cost, ctx.state, undefined);
}

/** The combatants with a reaction use pending on the action's ledger: spent, for every later reaction gate in the action. */
export function reactorsUsed(ledger: Pick<ResolutionLedger, "events"> | undefined): ReadonlySet<string> {
  const out = new Set<string>();
  for (const e of ledger?.events ?? []) if (e.kind === "reaction_use") out.add(e.carrier);
  return out;
}

/**
 * **The affordable check** (`event-engine-costs` D2): one predicate per cost kind, asked when the
 * cost is proposed, before any die is drawn. It reads the state the stage's listeners read plus
 * the costs still pending on the action's ledger, so two costs in one action can't overdraw.
 *   - `resource_cap_use`: the cadence cap has room (`resourceCapAvailable`), or this action already
 *     holds a use of the same (carrier, key) pending: one declaration is one use (D3,
 *     `decisions/0173`), so a second fire on the key costs nothing more.
 *   - `expend_charges`: the pool holds `amount` charges once the pending spends on it are counted at
 *     face value (a later replacement may waive them, but nothing can know that before the roll).
 *   - `action_points`: the turn budget holds `points`, and the slot the use stamps is open: the
 *     once-per-turn `actionId` (at `allowance`, default 1) or the Protocol ledger's
 *     `protocolActionId`; a reaction attack needs the reaction slot open and, when it names one,
 *     its named reaction unused.
 *   - `action_use`: the rate or pool has room once the uses pending on the same slot are counted.
 *   - `weapon`: a Limited weapon has a charge left, a Loading weapon is loaded, a Recharge item is
 *     charged.
 *   - `reaction_use`: the carrier's reaction slot is open, its named reaction unused (at
 *     `allowance`; never counted when `unlimited`), and no use of it is pending on the ledger.
 * `max` is the bound the proposer read off its source: the clause's cap, or the pool's size. Each
 * gate keeps its own refusal; this answers only whether the cost can be paid.
 */
export type ProposedCost =
  | ((ResourceCapUseEvent | ExpendChargesEvent) & { max: number })
  | (ActionPointsEvent & { allowance?: number })
  | ActionUseEvent
  | WeaponCostEvent
  | (ReactionUseEvent & { unlimited?: true; allowance?: number });

export function affordable(cost: ProposedCost, state: EncounterState, ledger: Pick<ResolutionLedger, "events"> | undefined): boolean {
  const pending = ledger?.events ?? [];
  const current = state.combatants[cost.carrier]?.current;
  switch (cost.kind) {
    case "expend_charges":
      return chargesLeft(cost, state, ledger) >= cost.amount;
    case "resource_cap_use":
      return pending.some((e) => e.kind === "resource_cap_use" && e.carrier === cost.carrier && e.key === cost.key) || resourceCapAvailable(current?.resources, cost.key, cost.max);
    case "action_points": {
      if (cost.reaction !== undefined) return reactionSlotOpen(state.activation, cost.carrier) && (cost.reaction.name === undefined || canUseReaction(state.activation.reactionUses[cost.carrier], cost.reaction.name));
      // The economy is inert with no turn acting: nothing to overdraw.
      const turn = state.activation.turn;
      if (turn.phase !== "acting") return true;
      const budget = turn.budget;
      if (cost.points > budget.actions) return false;
      if (cost.actionId !== undefined && !canUseAction(budget, cost.actionId, cost.allowance ?? 1)) return false;
      return cost.protocolActionId === undefined || canUseProtocolAction(budget, cost.protocolActionId, cost.allowance ?? 1);
    }
    case "action_use": {
      const slot = cost.pool ?? cost.key;
      const held = pending.filter((e) => e.kind === "action_use" && e.carrier === cost.carrier && e.grain === cost.grain && (e.pool ?? e.key) === slot).length;
      const room = (cost.max ?? 1) - held;
      return cost.grain === "round" ? canUseRoundAction(state.activation.actionRoundUses[cost.carrier], slot, room) : canUseSceneAction(current?.affordanceState, slot, room);
    }
    case "weapon":
      if (cost.spend === "discharge") return current?.affordanceState?.[cost.key]?.discharged !== true;
      if (cost.spend === "loading") return isWeaponLoaded(current?.resources, cost.key);
      return canUseLimitedWeapon(current?.affordanceState, cost.key, cost.limit ?? 0);
    case "reaction_use":
      return (
        !reactorsUsed(ledger).has(cost.carrier) &&
        reactionSlotOpen(state.activation, cost.carrier) &&
        (cost.unlimited === true || canUseReaction(state.activation.reactionUses[cost.carrier], cost.name, cost.allowance))
      );
  }
}

/** Charges left in the pool net of the spends pending on it: what the `expend_charges` atom reports when it can't pay. */
export function chargesLeft(pool: { carrier: string; pool: string; max: number }, state: EncounterState, ledger: Pick<ResolutionLedger, "events"> | undefined): number {
  let left = Math.max(0, pool.max - (state.combatants[pool.carrier]?.current.affordanceState?.[pool.pool]?.uses ?? 0));
  for (const e of ledger?.events ?? []) if (e.kind === "expend_charges" && e.carrier === pool.carrier && e.pool === pool.pool) left -= e.amount;
  return left;
}

/**
 * What settlement grounds:
 *   - `capSteps`: the cap counters, one per (carrier, key), in raise order;
 *   - `steps`: the charge spends (`affordance_use_set`) and the record of each waived one
 *     (`cost_waived`), in raise order, for the end of the resolution's step list.
 */
/** A settled declaration cost: who paid for declaring what, unless a replacement refunded it. */
export type SettledDeclaration = { carrier: string; action: string; refunded: boolean };

/** The steps a spend grounds into: charges and their waivers, and a weapon's discharge, unload and Limited count. */
export type SpendStep = AffordanceUseSetStep | CostWaivedStep | DischargeSetStep | ResourceSetStep | StatusRemoveStep | ResourceDeltaStep;

export type Settled = {
  capSteps: ResourceDeltaStep[];
  steps: SpendStep[];
  /** The reactions used, in raise order; omitted when none was. */
  reactions?: { combatantId: string; name: string }[];
  /** The declaration costs, in raise order; omitted when none was raised. */
  declarations?: SettledDeclaration[];
  /** The action-economy costs, in raise order, for {@link economyCommands}; omitted when none was raised. */
  economy?: EconomyCostEvent[];
};

/**
 * The board a settlement reads its replacements off (absent: no replacement applies), and the
 * policy the economy costs settle under ({@link economySpends}).
 */
export type SettleContext = { state: EncounterState; overlay: LoadedOverlay | undefined; policy?: SpendPolicy };

/** Whether one replacement clause applies to one event, against the settled resolution's facts. */
function matches(r: BoundReplacement<CostReplacementEffect>, event: ExpendChargesEvent, ledger: ResolutionLedger): boolean {
  const { match } = r.clause;
  if (match === undefined) return true;
  if (match.own === true && event.carrier !== r.carrier) return false;
  if (match.pool !== undefined && event.pool !== match.pool) return false;
  if (match.firstAttackRoll !== undefined) {
    const first = ledger.attackRolls?.[0];
    if (first === undefined) return false;
    if (match.firstAttackRoll.hit !== undefined && first.hit !== match.firstAttackRoll.hit) return false;
    if (match.firstAttackRoll.crit !== undefined && first.crit !== match.firstAttackRoll.crit) return false;
  }
  return true;
}

/**
 * Settle every pending event on the ledger into grounded steps, once, in raise order. A ledger is
 * one declaration's, so its cap uses collapse to one per (carrier, key), keeping the first: one
 * declaration is one use of a capped ability (`decisions/0173`, D3), on every shape. Each charge
 * spend is checked against the board's replacements in {@link replacementOrder}; the first that
 * matches prevents it, and the spend grounds as a `cost_waived` record instead.
 */
export function settleEvents(ledger: ResolutionLedger, ctx?: SettleContext): Settled {
  if (ledger.settled === true) throw new Error("settleEvents: the ledger already settled");
  ledger.settled = true;
  const seen = new Set<string>();
  const capSteps: ResourceDeltaStep[] = [];
  const spends: SpendEvent[] = [];
  const declarations: SettledDeclaration[] = [];
  const reactions: { combatantId: string; name: string }[] = [];
  const economy: EconomyCostEvent[] = [];
  for (const event of ledger.events ?? []) {
    if (event.kind === "reaction_use") {
      reactions.push({ combatantId: event.carrier, name: event.name });
      continue;
    }
    if (event.kind === "action_points" || event.kind === "action_use" || event.kind === "earmark") {
      if (economySpends(event, ctx)) economy.push(event);
      continue;
    }
    if (event.kind === "expend_charges" || event.kind === "weapon" || event.kind === "consume_status" || event.kind === "counter") spends.push(event);
    else if (event.kind === "declaration") declarations.push({ carrier: event.carrier, action: event.action, refunded: event.refunded === true });
    else {
      const pair = `${event.carrier}\u0000${event.key}`;
      if (seen.has(pair)) continue;
      seen.add(pair);
      capSteps.push({ op: "resource_delta", target: event.carrier, key: resourceCapKey(event.key), delta: 1 });
    }
  }
  return { capSteps, steps: spends.length > 0 ? settleSpends(spends, ledger, ctx) : [], ...(reactions.length > 0 ? { reactions } : {}), ...(declarations.length > 0 ? { declarations } : {}), ...(economy.length > 0 ? { economy } : {}) };
}

/** F2a's gather order within one carrier: weapon/tag → pilot → status → system (`decisions/0212` item 2). */
const VECTOR_RANK: Record<string, number> = { pilotEffects: 1, statuses: 2, systems: 3 };

/**
 * The replacements in `decisions/0212` item 2's order for one event: the event's own carrier's
 * first, then everyone else's by initiative (`state.order`, an absent combatant last), then gather
 * order. Replacement clauses carry no `priority`. Generic over the clause so a roll's and a check's replacements order the same way. Stable, so ties keep the board walk's order.
 */
export function replacementOrder<R extends { carrier: string; vector: string }>(replacements: readonly R[], subject: string, state: EncounterState | undefined): R[] {
  const initiative = (id: string): number => {
    const i = state?.order.indexOf(id) ?? -1;
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  // One tuple key: spender first, initiative, then (within a carrier, since the board walk keeps
  // each carrier's clauses together) gather vector, then the walk's own order.
  const firstAt = new Map<string, number>();
  replacements.forEach((r, idx) => firstAt.has(r.carrier) || firstAt.set(r.carrier, idx));
  const key = (r: R, idx: number): number[] => [r.carrier === subject ? 0 : 1, initiative(r.carrier), firstAt.get(r.carrier)!, VECTOR_RANK[r.vector] ?? 0, idx];
  return replacements
    .map((r, idx) => ({ r, k: key(r, idx) }))
    .sort((a, b) => {
      for (let i = 0; i < a.k.length; i++) if (a.k[i] !== b.k[i]) return a.k[i]! - b.k[i]!;
      return 0;
    })
    .map(({ r }) => r);
}

/** The board's cost replacements (Ammo Case III's Efficiency): every `on: "expend_charges"` clause. */
function costReplacements(state: EncounterState, overlay: LoadedOverlay): BoundReplacement<CostReplacementEffect>[] {
  return gatherReplacements(state, overlay).filter((r): r is BoundReplacement<CostReplacementEffect> => r.clause.on === "expend_charges");
}

/**
 * Ground the spends in raise order. A charge spend is waived by the first matching replacement in
 * F2a order, or is one `affordance_use_set` chained on its pool; a weapon cost grounds per
 * {@link WeaponCostEvent}. Running counters chain two spends on one ledger slot.
 */
type SpendEvent = ExpendChargesEvent | WeaponCostEvent | ConsumeStatusEvent | CounterCostEvent;

function settleSpends(spends: readonly SpendEvent[], ledger: ResolutionLedger, ctx: SettleContext | undefined): SpendStep[] {
  const replacements = ctx?.overlay !== undefined ? costReplacements(ctx.state, ctx.overlay) : [];
  const out: SpendStep[] = [];
  const running = new Map<string, number | undefined>();
  const spent = new Set<string>();
  const usesOf = (carrier: string, key: string) => {
    const slot = `${carrier}\u0000${key}`;
    return running.has(slot) ? running.get(slot) : ctx?.state.combatants[carrier]?.current.affordanceState?.[key]?.uses;
  };
  const setUses = ({ carrier, key }: { carrier: string; key: string }, prev: number | undefined, uses: number): AffordanceUseSetStep => {
    running.set(`${carrier}\u0000${key}`, uses);
    return { op: "affordance_use_set", target: carrier, key, uses, ...(prev !== undefined ? { prev } : {}) };
  };
  for (const event of spends) {
    if (event.kind === "weapon") {
      const step = groundWeaponCost(event, ctx, { spent, usesOf, setUses });
      if (step !== undefined) out.push(step);
      continue;
    }
    if (event.kind === "counter") {
      out.push({ op: "resource_delta", target: event.carrier, key: event.key, delta: event.delta });
      continue;
    }
    if (event.kind === "consume_status") {
      const once = `consume\u0000${event.carrier}\u0000${event.status}`;
      if (spent.has(once)) continue;
      spent.add(once);
      out.push({ op: "status_remove", target: event.carrier, status: event.status, ...event.facets });
      continue;
    }
    const by = replacementOrder(replacements, event.carrier, ctx?.state).find((r) => matches(r, event, ledger));
    if (by !== undefined) {
      out.push({ op: "cost_waived", target: event.carrier, key: event.pool, amount: event.amount, by: { carrier: by.carrier, id: by.entityId, name: by.clause.name } });
      continue;
    }
    const prev = event.read !== undefined && !running.has(`${event.carrier}\u0000${event.pool}`) ? event.read.uses : usesOf(event.carrier, event.pool);
    out.push(setUses({ carrier: event.carrier, key: event.pool }, prev, (prev ?? 0) + event.amount));
  }
  return out;
}

type WeaponGrounding = {
  spent: Set<string>;
  usesOf: (carrier: string, key: string) => number | undefined;
  setUses: (slot: { carrier: string; key: string }, prev: number | undefined, uses: number) => AffordanceUseSetStep;
};

/** One weapon cost's step, or none when it is already spent (see {@link WeaponCostEvent}). */
function groundWeaponCost(event: WeaponCostEvent, ctx: SettleContext | undefined, g: WeaponGrounding): SpendStep | undefined {
  if (ctx === undefined) throw new Error(`settleEvents: a ${event.spend} cost needs the state it settles over`);
  const once = `${event.spend}\u0000${event.carrier}\u0000${event.key}`;
  if (event.spend !== "limited" && g.spent.has(once)) return undefined;
  g.spent.add(once);
  const current = ctx.state.combatants[event.carrier]?.current;
  if (event.spend === "discharge") {
    if (current?.affordanceState?.[event.key]?.discharged) return undefined;
    return { op: "discharge_set", target: event.carrier, key: event.key, discharged: true };
  }
  if (event.spend === "loading") {
    if (!isWeaponLoaded(current?.resources, event.key)) return undefined;
    // `prevAbsent` when the key never existed, so undo restores true absence (`ResourceSetStep.prevAbsent`).
    const key = loadedResourceKey(event.key);
    const had = current?.resources !== undefined && Object.hasOwn(current.resources, key);
    return { op: "resource_set", target: event.carrier, key, val: 0, prev: had ? current!.resources![key]! : 0, ...(had ? {} : { prevAbsent: true }) };
  }
  const prev = g.usesOf(event.carrier, event.key);
  if ((prev ?? 0) >= (event.limit ?? 0)) return undefined;
  return g.setUses(event, prev, (prev ?? 0) + 1);
}

/**
 * The replacement that would waive `event` if the declaration's first attack roll crits, and only
 * then: the attack forecast's "waived on a crit (Efficiency)". `undefined` when no replacement
 * waives it on a crit, or when one would waive it on any hit.
 */
export function waiverOnCrit(event: ExpendChargesEvent, ctx: SettleContext): string | undefined {
  if (ctx.overlay === undefined) return undefined;
  const replacements = replacementOrder(costReplacements(ctx.state, ctx.overlay), event.carrier, ctx.state);
  const on = (crit: boolean) => replacements.find((r) => matches(r, event, { choices: {}, settlement: "discards", attackRolls: [{ target: "", hit: true, crit }] }));
  return on(false) === undefined ? on(true)?.clause.name : undefined;
}
